import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, BreadcrumbItem, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import authService from 'src/services/authService';
import {
  fetchAssetByIdApi,
  AssetDetail,
  addControlsToAssetApi,
  removeControlsFromAssetApi,
} from 'src/services/assetsServices';

let BASE_URL = process.env.REACT_APP_API_URL;
async function loadConfig() {
  try {
    const publicUrl = process.env.PUBLIC_URL || '';
    const response = await fetch(`${publicUrl}/config.json`);
    const config = await response.json();
    BASE_URL = config.API_URL || BASE_URL;
  } catch (error) {
    console.error('Failed to load config.json', error);
  }
}
loadConfig();

const AssetDetails: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [asset, setAsset] = useState<AssetDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [controls, setControls] = useState<any[]>([]);
  const [selectedControls, setSelectedControls] = useState<string[]>([]);
  const [showControlModal, setShowControlModal] = useState(false);
  const [isAddingControls, setIsAddingControls] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [removingControls, setRemovingControls] = useState(false);
  const [deleteControlId, setDeleteControlId] = useState<string | null>(null);
  const [controlsPage, setControlsPage] = useState(1);
  const [controlsPerPage] = useState(10);
  const [controlsTotalPages, setControlsTotalPages] = useState(1);
  const [controlsTotal, setControlsTotal] = useState(0);
  const [loadingControls, setLoadingControls] = useState(false);

  useEffect(() => {
    if (!id) return;

    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchAssetByIdApi(id);
        setAsset(data);
      } catch (error: any) {
        toast.error(
          error?.message ||
            t('assets.details.loadFailed', { defaultValue: 'Failed to load asset' }),
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, t]);

  // Fetch controls with pagination
  const fetchControls = async (page: number = controlsPage) => {
    setLoadingControls(true);
    try {
      // Backend expects pageNo (0-indexed) and perPage
      const pageNo = page - 1; // Convert to 0-indexed
      const url = `${BASE_URL}/controls?pageNo=${pageNo}&perPage=${controlsPerPage}${
        searchQuery ? `&query=${encodeURIComponent(searchQuery)}` : ''
      }`;
      const response = await authService.apiCall(url, { method: 'GET' });
      const data = await response.json();
      if (response.ok && data.success) {
        setControls(data.data || []);
        setControlsTotalPages(data.totalPages || 1);
        setControlsTotal(data.totalCount || 0);
        setControlsPage(page);
      } else {
        throw new Error(data.message || 'Failed to fetch controls');
      }
    } catch (error: any) {
      console.error('Error fetching controls:', error);
      toast.error(error?.message || 'Failed to fetch controls');
    } finally {
      setLoadingControls(false);
    }
  };

  // Handle search - reset to page 1
  const handleSearch = () => {
    setControlsPage(1);
    fetchControls(1);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= controlsTotalPages) {
      fetchControls(newPage);
    }
  };

  // Toggle control selection
  const toggleControlSelection = (controlId: string) => {
    setSelectedControls((prev) =>
      prev.includes(controlId)
        ? prev.filter((id) => id !== controlId)
        : [...prev, controlId],
    );
  };

  // Add selected controls to asset
  const handleAddControls = async () => {
    if (!id || selectedControls.length === 0) return;

    setIsAddingControls(true);
    try {
      // Get current control IDs to avoid duplicates
      const currentControlIds =
        asset?.controls?.map((c: any) => c.id || c.ID) || [];
      const newControlIds = selectedControls.filter(
        (id) => !currentControlIds.includes(id),
      );

      if (newControlIds.length === 0) {
        toast.info(
          'All selected controls are already associated with this asset',
        );
        setShowControlModal(false);
        return;
      }

      const response = await addControlsToAssetApi(id, {
        controlIds: newControlIds,
      });

      if (response.success) {
        toast.success('Controls added successfully');
        // Refresh asset data
        const res = await fetchAssetByIdApi(id);
        setAsset(res);
        setSelectedControls([]);
        setShowControlModal(false);
      } else {
        throw new Error(response.message || 'Failed to add controls');
      }
    } catch (error: any) {
      console.error('Error adding controls:', error);
      toast.error(error?.message || 'Failed to add controls');
    } finally {
      setIsAddingControls(false);
    }
  };

  // Remove controls from asset
  const handleRemoveControls = async (controlIds: string[]) => {
    if (!id || controlIds.length === 0) return;

    setRemovingControls(true);
    try {
      const response = await removeControlsFromAssetApi(id, { controlIds });

      if (response.success) {
        toast.success('Controls removed successfully');
        // Refresh asset data
        const res = await fetchAssetByIdApi(id);
        setAsset(res);
        setDeleteControlId(null);
      } else {
        throw new Error(response.message || 'Failed to remove controls');
      }
    } catch (error: any) {
      console.error('Error removing controls:', error);
      toast.error(error?.message || 'Failed to remove controls');
    } finally {
      setRemovingControls(false);
    }
  };

  // Initialize selected controls when asset loads
  useEffect(() => {
    if (asset?.controls) {
      setSelectedControls(
        asset.controls.map((c: any) => c.id || c.ID).filter(Boolean),
      );
    }
  }, [asset]);

  return (
    <div className="root">
      <Breadcrumb>
        <BreadcrumbItem>{t('header.you_are_here')}</BreadcrumbItem>
        <span className="mx-2">/</span>
        <BreadcrumbItem onClick={() => navigate('/assets')} style={{ cursor: 'pointer' }}>
          {t('assets.titlePage') || 'Assets Management'}
        </BreadcrumbItem>
        <span className="mx-2">/</span>
        <BreadcrumbItem active>
          {t('assets.details.titlePage', { defaultValue: 'Asset Details' })}
        </BreadcrumbItem>
      </Breadcrumb>

      <h4 className="mb-lg px-2">
        {t('assets.details.titlePage', { defaultValue: 'Asset Details' })}
      </h4>

      <div
        className="container_info"
        style={{ padding: '40px 20px', backgroundColor: 'var(--widget-bg)', zoom: 0.9 }}
      >
        {loading && (
          <div className="text-center py-4">
            {t('common.loading', { defaultValue: 'Loading...' })}
          </div>
        )}

        {!loading && !asset && (
          <div className="text-center py-4 text-danger">
            {t('assets.details.notFound', { defaultValue: 'Asset not found' })}
          </div>
        )}

        {!loading && asset && (
          <>
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <h5>{asset.assetName}</h5>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => navigate('/assets')}
              >
                {t('common.back', { defaultValue: 'Back' })}
              </button>
            </div>

            {/* General information and Asset Services */}
            <div className="row mb-4">
              {/* General Information Column */}
              <div className="col-md-6">
                <h5 className="mb-3">
                  {t('assets.details.generalInformation', {
                    defaultValue: 'General Information',
                  })}
                </h5>
                <div className="mb-3">
                  <strong>{t('assets.details.id', { defaultValue: 'ID' })}:</strong>{' '}
                  {asset.id}
                </div>
                <div className="mb-3">
                  <strong>
                    {t('assets.create.fields.type', { defaultValue: 'Type' })}:
                  </strong>{' '}
                  {asset.assetType?.name || '-'}
                </div>
                <div className="mb-3">
                  <strong>
                    {t('assets.create.fields.owner', { defaultValue: 'Owner' })}:
                  </strong>{' '}
                  {asset.owner?.name || '-'}
                </div>
                <div className="mb-3">
                  <strong>
                    {t('assets.create.fields.custodian', { defaultValue: 'Custodian' })}:
                  </strong>{' '}
                  {asset.custodian?.name || '-'}
                </div>
                <div className="mb-3">
                  <strong>
                    {t('assets.create.fields.posture', { defaultValue: 'Posture' })}:
                  </strong>{' '}
                  {asset.posture || '-'}
                </div>
                <div className="mb-3">
                  <strong>
                    {t('assets.create.fields.compliance', { defaultValue: 'Compliance' })}:
                  </strong>{' '}
                  {asset.compliance || '-'}
                </div>
                <div className="mb-3">
                  <strong>
                    {t('assets.create.fields.date', { defaultValue: 'Date' })}:
                  </strong>{' '}
                  {asset.date ? asset.date.split('T')[0] : '-'}
                </div>
              </div>

              {/* Asset Services Column */}
              <div className="col-md-6">
                <h5 className="mb-3">
                  {t('assets.details.serviceRepo', {
                    defaultValue: 'Asset Services',
                  })}
                </h5>
                {(() => {
                  // Parse assetType.fields if it's a JSON string
                  let serviceFields: any[] = [];
                  if (asset.assetType?.fields) {
                    try {
                      const fieldsData = asset.assetType.fields;
                      serviceFields =
                        typeof fieldsData === 'string'
                          ? JSON.parse(fieldsData)
                          : Array.isArray(fieldsData)
                          ? fieldsData
                          : [];
                    } catch {
                      serviceFields = [];
                    }
                  }

                  // Parse asset.fields if it's a JSON string
                  let assetFields: Record<string, any> = {};
                  if (asset.fields) {
                    try {
                      const fieldsData = asset.fields;
                      assetFields =
                        typeof fieldsData === 'string'
                          ? JSON.parse(fieldsData)
                          : typeof fieldsData === 'object'
                          ? fieldsData
                          : {};
                    } catch {
                      assetFields = {};
                    }
                  }

                  if (serviceFields.length === 0) {
                    return (
                      <div className="text-center py-4 bg-light rounded">
                        <p className="text-muted mb-0">
                          {t('assets.details.empty.servicesTitle', {
                            defaultValue: 'No service fields defined for this asset type.',
                          })}
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-2">
                      {serviceFields.map((field: any) => {
                        const fieldValue = assetFields[field.key];
                        let displayValue: any = '-';
                        if (fieldValue !== undefined && fieldValue !== null) {
                          if (field.type === 'boolean') {
                            displayValue = fieldValue ? 'Yes' : 'No';
                          } else if (field.type === 'date') {
                            displayValue = new Date(fieldValue).toLocaleDateString();
                          } else {
                            displayValue = String(fieldValue);
                          }
                        }
                        return (
                          <div
                            key={field.key}
                            className="d-flex justify-content-between align-items-center py-2 px-3 bg-light rounded mb-2"
                            style={{
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }}
                          >
                            <span className="small font-weight-medium" style={{ color: '#495057' }}>
                              {field.label}:
                            </span>
                            <span className="font-weight-semibold text-dark">
                              {displayValue === '-' ? (
                                <span className="text-muted font-italic">-</span>
                              ) : (
                                displayValue
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Related controls */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  {t('assets.details.relatedControls', {
                    defaultValue: 'Related Controls',
                  })}
                </h5>
                <div className="d-flex align-items-center gap-3">
                  <button
                    onClick={() => {
                      setControlsPage(1);
                      setSearchQuery('');
                      fetchControls(1);
                      setShowControlModal(true);
                    }}
                    className="btn btn-primary"
                    style={{
                      background:
                        'linear-gradient(to right, #f97316, #ea580c)',
                      border: 'none',
                      padding: '8px 24px',
                      borderRadius: '12px',
                      fontWeight: 600,
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    }}
                  >
                    <svg
                      className="me-2"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    {t('assets.details.addControls', {
                      defaultValue: 'Add Controls',
                    })}
                  </button>
                  <span className="badge bg-secondary">
                    {(asset as any).controls?.length || 0}{' '}
                    {t('assets.details.controlsCountLabel', {
                      defaultValue: 'controls',
                    })}
                  </span>
                </div>
              </div>
              {Array.isArray((asset as any).controls) &&
              (asset as any).controls.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped table-bordered mt-2">
                    <thead className="table-light">
                      <tr>
                        <th>
                          {t('assets.details.table.code', {
                            defaultValue: 'Code',
                          })}
                        </th>
                        <th>
                          {t('assets.details.table.name', {
                            defaultValue: 'Name',
                          })}
                        </th>
                        <th>
                          {t('assets.details.table.actions', {
                            defaultValue: 'Actions',
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(asset as any).controls.map((control: any) => (
                        <tr key={control.id || control.ID}>
                          <td>{control.code || control.Code}</td>
                          <td>{control.name || control.Name}</td>
                          <td>
                            <button
                              onClick={() =>
                                setDeleteControlId(control.id || control.ID)
                              }
                              disabled={removingControls}
                              className="btn btn-sm btn-link text-danger p-0"
                              title={
                                (t('assets.details.removeControl', {
                                  defaultValue: 'Remove Control',
                                }) as string) || 'Remove Control'
                              }
                            >
                              <svg
                                width="16"
                                height="16"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 bg-light rounded">
                  <p className="text-muted mb-0">
                    {t('assets.details.empty.controlsTitle', {
                      defaultValue: 'No related controls.',
                    })}
                  </p>
                </div>
              )}
            </div>

            {/* Related risks */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  {t('assets.details.relatedRisks', {
                    defaultValue: 'Related Risks',
                  })}
                </h5>
                <span className="badge bg-secondary">
                  {(asset as any).relatedRisks?.length || 0}{' '}
                  {t('assets.details.risksCountLabel', {
                    defaultValue: 'risks',
                  })}
                </span>
              </div>
              {Array.isArray((asset as any).relatedRisks) &&
              (asset as any).relatedRisks.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped table-bordered mt-2">
                    <thead className="table-light">
                      <tr>
                        <th>{t('assets.details.table.code', { defaultValue: 'Code' })}</th>
                        <th>{t('assets.details.table.name', { defaultValue: 'Name' })}</th>
                        <th>
                          {t('assets.details.table.description', { defaultValue: 'Description' })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(asset as any).relatedRisks.map((risk: any) => (
                        <tr key={risk.id}>
                          <td>{risk.code}</td>
                          <td>{risk.name}</td>
                          <td>{risk.description || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted mt-2">
                  {t('assets.details.empty.risksTitle', {
                    defaultValue: 'No related risks.',
                  })}
                </p>
              )}
            </div>

            {/* Related processes */}
            <div className="mb-2">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  {t('assets.details.relatedProcesses', {
                    defaultValue: 'Related Processes',
                  })}
                </h5>
                <span className="badge bg-secondary">
                  {(asset as any).relatedProcesses?.length || 0}{' '}
                  {t('assets.details.processesCountLabel', {
                    defaultValue: 'processes',
                  })}
                </span>
              </div>
              {Array.isArray((asset as any).relatedProcesses) &&
              (asset as any).relatedProcesses.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped table-bordered mt-2">
                    <thead className="table-light">
                      <tr>
                        <th>{t('assets.details.table.code', { defaultValue: 'Code' })}</th>
                        <th>{t('assets.details.table.name', { defaultValue: 'Name' })}</th>
                        <th>
                          {t('assets.details.table.description', { defaultValue: 'Description' })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(asset as any).relatedProcesses.map((process: any) => (
                        <tr key={process.id}>
                          <td>{process.code}</td>
                          <td>{process.name}</td>
                          <td>{process.description || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted mt-2">
                  {t('assets.details.empty.processesTitle', {
                    defaultValue: 'No related processes.',
                  })}
                </p>
              )}
            </div>

            {/* Control Selection Modal */}
            <Modal
              isOpen={showControlModal}
              toggle={() => setShowControlModal(false)}
              size="lg"
            >
              <ModalHeader toggle={() => setShowControlModal(false)}>
                {t('assets.details.selectControlsTitle', {
                  defaultValue: 'Select Controls',
                })}
              </ModalHeader>
              <ModalBody>
                <div className="mb-3">
                  <div className="d-flex gap-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={
                        (t('assets.details.searchControlsPlaceholder', {
                          defaultValue: 'Search controls...',
                        }) as string) || 'Search controls...'
                      }
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch();
                        }
                      }}
                    />
                    <button
                      onClick={handleSearch}
                      className="btn btn-primary"
                      disabled={loadingControls}
                    >
                      {t('assets.details.search', { defaultValue: 'Search' })}
                    </button>
                  </div>
                </div>

                {loadingControls ? (
                  <div className="text-center py-4">
                    {t('common.loading', { defaultValue: 'Loading...' })}
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        overflowX: 'auto',
                        border: '1px solid #dee2e6',
                        borderRadius: '0.25rem',
                      }}
                    >
                      <table className="table table-hover mb-0" style={{ position: 'relative' }}>
                        <thead className="table-light" style={{ position: 'relative' }}>
                          <tr>
                            <th style={{ position: 'relative' }}>
                              {t('assets.details.table.select', {
                                defaultValue: 'Select',
                              })}
                            </th>
                            <th style={{ position: 'relative' }}>
                              {t('assets.details.table.code', {
                                defaultValue: 'Code',
                              })}
                            </th>
                            <th style={{ position: 'relative' }}>
                              {t('assets.details.table.name', {
                                defaultValue: 'Name',
                              })}
                            </th>
                          </tr>
                        </thead>
                        <tbody style={{ position: 'relative' }}>
                          {controls.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="text-center text-muted py-4">
                                {t('assets.details.noControlsFound', {
                                  defaultValue: 'No controls found',
                                })}
                              </td>
                            </tr>
                          ) : (
                            controls.map((control) => (
                              <tr key={control.ID || control.id} style={{ position: 'relative' }}>
                                <td style={{ position: 'relative' }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedControls.includes(
                                      control.ID || control.id,
                                    )}
                                    onChange={() =>
                                      toggleControlSelection(
                                        control.ID || control.id,
                                      )
                                    }
                                    style={{
                                      position: 'relative',
                                      margin: 0,
                                      cursor: 'pointer',
                                      width: '18px',
                                      height: '18px',
                                    }}
                                  />
                                </td>
                                <td style={{ position: 'relative' }}>
                                  {control.Code || control.code}
                                </td>
                                <td style={{ position: 'relative' }}>
                                  {control.Name || control.name}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {controlsTotalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted small">
                          {t('assets.details.showing', {
                            defaultValue: 'Showing',
                          })}{' '}
                          {(controlsPage - 1) * controlsPerPage + 1} -{' '}
                          {Math.min(controlsPage * controlsPerPage, controlsTotal)}{' '}
                          {t('assets.details.of', { defaultValue: 'of' })}{' '}
                          {controlsTotal}{' '}
                          {t('assets.details.controls', {
                            defaultValue: 'controls',
                          })}
                        </div>
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li
                              className={`page-item ${
                                controlsPage === 1 ? 'disabled' : ''
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(controlsPage - 1)}
                                disabled={controlsPage === 1}
                              >
                                {t('common.previous', { defaultValue: 'Previous' })}
                              </button>
                            </li>
                            {Array.from({ length: controlsTotalPages }, (_, i) => i + 1)
                              .filter(
                                (page) =>
                                  page === 1 ||
                                  page === controlsTotalPages ||
                                  (page >= controlsPage - 1 && page <= controlsPage + 1),
                              )
                              .map((page, index, array) => (
                                <React.Fragment key={page}>
                                  {index > 0 && array[index - 1] !== page - 1 && (
                                    <li className="page-item disabled">
                                      <span className="page-link">...</span>
                                    </li>
                                  )}
                                  <li
                                    className={`page-item ${
                                      controlsPage === page ? 'active' : ''
                                    }`}
                                  >
                                    <button
                                      className="page-link"
                                      onClick={() => handlePageChange(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                </React.Fragment>
                              ))}
                            <li
                              className={`page-item ${
                                controlsPage === controlsTotalPages ? 'disabled' : ''
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(controlsPage + 1)}
                                disabled={controlsPage === controlsTotalPages}
                              >
                                {t('common.next', { defaultValue: 'Next' })}
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  color="secondary"
                  onClick={() => setShowControlModal(false)}
                >
                  {t('assets.modal.cancel', { defaultValue: 'Cancel' })}
                </Button>
                <Button
                  color="primary"
                  onClick={handleAddControls}
                  disabled={isAddingControls || selectedControls.length === 0}
                >
                  {isAddingControls
                    ? t('assets.details.adding', { defaultValue: 'Adding...' })
                    : t('assets.details.addSelectedControls', {
                        defaultValue: 'Add Selected Controls',
                      })}
                </Button>
              </ModalFooter>
            </Modal>

            {/* Delete Control Confirmation Modal */}
            <Modal
              isOpen={!!deleteControlId}
              toggle={() => setDeleteControlId(null)}
            >
              <ModalHeader toggle={() => setDeleteControlId(null)}>
                {t('assets.details.removeControl', {
                  defaultValue: 'Remove Control',
                })}
              </ModalHeader>
              <ModalBody>
                <p>
                  {t('assets.details.removeControlMessage', {
                    defaultValue:
                      'Are you sure you want to remove this control from the asset?',
                  })}
                </p>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="secondary"
                  onClick={() => setDeleteControlId(null)}
                >
                  {t('assets.modal.cancel', { defaultValue: 'Cancel' })}
                </Button>
                <Button
                  color="danger"
                  onClick={() => {
                    if (deleteControlId) {
                      handleRemoveControls([deleteControlId]);
                    }
                  }}
                  disabled={removingControls}
                >
                  {removingControls
                    ? t('assets.details.removing', { defaultValue: 'Removing...' })
                    : t('assets.details.remove', { defaultValue: 'Remove' })}
                </Button>
              </ModalFooter>
            </Modal>
          </>
        )}
      </div>
    </div>
  );
};

export default AssetDetails;


